import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { STATUS } from "./FeriePage";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Timer, Plus, X, CalendarIcon } from "lucide-react";

const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; } };

const StraordinariPage = () => {
  const [list, setList] = useState([]);
  const [day, setDay] = useState(null);
  const [hours, setHours] = useState("");
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/overtime/mine").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalApproved = list.filter((o) => o.status === "approvata").reduce((s, o) => s + (o.hours || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!day) { toast.error("Seleziona la data"); return; }
    if (!hours || Number(hours) <= 0) { toast.error("Indica le ore di straordinario"); return; }
    setSubmitting(true);
    try {
      await api.post("/overtime", { date: format(day, "yyyy-MM-dd"), hours: Number(hours), reason });
      toast.success("Straordinario registrato! In attesa di approvazione.");
      setDay(null); setHours(""); setReason(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore nell'invio"); }
    finally { setSubmitting(false); }
  };

  const cancel = async (id) => {
    try { await api.put(`/overtime/${id}/cancel`); toast.success("Richiesta annullata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="straordinari-page">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><Timer size={24} /> Straordinari</h1>
        <p className="text-[14px] text-gray-500 mb-6">Registra le ore di lavoro extra. Approvazione da parte del responsabile.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-lg p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#e67e221a", color: "#e67e22" }}><Timer size={24} /></span>
            <div>
              <div className="text-[26px] font-black text-[#16345c] leading-none">{totalApproved}</div>
              <div className="text-[12px] text-gray-500">ore approvate</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-32">
              <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Nuovo straordinario</h2>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Giorno</label>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="ot-date-trigger" className="w-full flex items-center gap-2 border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] text-left hover:border-[#16345c] transition-colors">
                        <CalendarIcon size={16} className="text-gray-400" />
                        {day ? format(day, "dd/MM/yyyy") : <span className="text-gray-400">Seleziona data</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={day} onSelect={(d) => { setDay(d); setOpen(false); }} locale={it} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Ore</label>
                  <input type="number" min="0.5" max="12" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="ot-hours" placeholder="es. 2" className="w-full border border-gray-300 rounded-sm px-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Motivo</label>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="ot-reason" placeholder="Motivo dello straordinario..." className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
                </div>
                <button type="submit" disabled={submitting} data-testid="ot-submit" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                  {submitting ? "Invio..." : "Registra straordinario"}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4">Le mie registrazioni</h2>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <Timer size={40} className="mx-auto mb-3 opacity-40" /> Nessuno straordinario registrato
              </div>
            ) : (
              <div className="space-y-3" data-testid="ot-list">
                {list.map((o) => (
                  <div key={o.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[14px] font-bold text-[#16345c]">{o.hours} ore</span>
                        <span className="text-[13px] text-gray-600">· {fmt(o.date)}</span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS[o.status]?.cls}`}>{STATUS[o.status]?.label}</span>
                      </div>
                      {o.reason && <div className="text-[12.5px] text-gray-400 mt-1 italic">"{o.reason}"</div>}
                      {o.decision_note && <div className="text-[12px] text-gray-500 mt-1"><b>{o.approver_name}:</b> {o.decision_note}</div>}
                    </div>
                    {o.status === "in_attesa" && (
                      <button onClick={() => cancel(o.id)} data-testid={`ot-cancel-${o.id}`} className="shrink-0 flex items-center gap-1 text-[12px] font-semibold text-[#c00510] hover:bg-red-50 px-2 py-1 rounded-sm transition-colors"><X size={14} /> Annulla</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StraordinariPage;
